import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useToast } from "./Toast";
import { Loading } from "./Loading";
import { SelectChangeEvent } from "@mui/material/Select";
import { roles } from "../pages/Staff";

const AddStaff: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    role: "",
    otherRole: "",
  });

  const formatPhoneNumber = (phone: string) => {
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, "");
    // Remove +91 prefix if present
    return cleaned.replace(/^91/, "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    try {
      const token = user?.accessToken;
      const endpoint = `${process.env.REACT_APP_API_HOST}users/company/add-staff`;

      // Format phone number before sending
      const formattedPhone = formatPhoneNumber(formData.phoneNumber);

      const payload = {
        name: formData.name,
        phoneNumber: formattedPhone,
        role: formData.role === "OTHER" ? formData.otherRole : formData.role,
      };

      const response = await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        showToast("Staff added successfully", "success");
        navigate("/staff");
      } else if (!response.data?.isSuccess && response.data?.userExists) {
        showToast("User already exists in the system", "error");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.errors) {
          const validationErrors = error.response.data.errors;
          const errorMessage = validationErrors
            .map(
              (err: { field: string; message: string }) =>
                `${err.field}: ${err.message}`
            )
            .join(", ");
          showToast(errorMessage, "error");
        } else {
          showToast(
            error.response?.data?.message || "Failed to add staff",
            "error"
          );
        }
      } else {
        showToast("Failed to add staff", "error");
      }
      console.error("Error adding staff:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "role" && value !== "OTHER" && { otherRole: "" }),
    }));
  };

  if (isLoading) {
    return <Loading message="Adding staff..." />;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" component="h1">
          Add New Staff
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/staff")}>
          Back to Staff List
        </Button>
      </Box>

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="name"
                label="Name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="phoneNumber"
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  label="Role"
                  onChange={handleSelectChange}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formData.role === "OTHER" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  name="otherRole"
                  label="Specify Role"
                  value={formData.otherRole}
                  onChange={handleInputChange}
                  placeholder="Enter custom role"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => navigate("/staff")}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={isLoading}>
                  Add Staff
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddStaff;
